import {
    TokenUsage,
    TokenUsageFunctionNameMap,
    TokenUsageView,
    TokenUsageViewDetails,
} from '../../../../../libs/models/TokenUsage';
import { useAppSelector } from '../../../../../redux/app/hooks';
import { RootState } from '../../../../../redux/app/store';
import { TypingIndicator } from '../TypingIndicator/TypingIndicator';
import { TokenUsageBar } from './TokenUsageBar';
import { TokenUsageLegendItem } from './TokenUsageLegendItem';

interface ITokenUsageGraph {
    tokenUsage: TokenUsage;
    promptView?: boolean;
}

/* const contrastColors = [
    tokens.colorPaletteBlueBackground2,
    tokens.colorPaletteBlueForeground2,
    tokens.colorPaletteBlueBorderActive,
]; */

const contrastColors = [
    'bg-blue-100', // Replace with appropriate Tailwind class if needed
    'bg-blue-200',
    'bg-blue-300',
];

const tailwindPrimaryColors = [
    'bg-primary-900 dark:bg-primary-50',
    'bg-primary-800 dark:bg-primary-100',
    'bg-primary-700 dark:bg-primary-200',
    'bg-primary-600 dark:bg-primary-300',
    'bg-primary-500 dark:bg-primary-400',
    /* 'bg-primary-400 dark:bg-primary-500',
    'bg-primary-300 dark:bg-primary-600',
    'bg-primary-200 dark:bg-primary-700',
    'bg-primary-100 dark:bg-primary-800',
    'bg-primary-50 dark:bg-primary-900', */
];

export const TokenUsageGraph: React.FC<ITokenUsageGraph> = ({ promptView, tokenUsage }) => {
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const loadingResponse =
        selectedId !== '' && conversations[selectedId].botResponseStatus && Object.entries(tokenUsage).length === 0;

    const responseGenerationView: TokenUsageView = {};
    const memoryGenerationView: TokenUsageView = {};
    let memoryGenerationUsage = 0;
    let responseGenerationUsage = 0;

    /* const graphColors = {
        brand: {
            // Color index of semanticKernelBrandRamp array defined in styles.ts
            legend: 120 as Brands,
            index: 120 as Brands,
            getNextIndex: () => {
                const nextIndex = graphColors.brand.index - 20;
                return (nextIndex < 0 ? 160 : nextIndex) as Brands;
            },
        },
        contrast: {
            // Color index of contrastColors array defined above
            legend: 0,
            index: 0,
            getNextIndex: () => {
                return graphColors.contrast.index++ % 3;
            },
        },
    }; */

    const graphColors = {
        brand: {
            // Color index of tailwindPrimaryColors array defined above
            legend: 4,
            index: 4,
            getNextIndex: () => {
                const nextIndex = graphColors.brand.index - 1;
                return nextIndex < 0 ? 4 : nextIndex;
            },
        },
        contrast: {
            // Color index of contrastColors array defined above
            legend: 0,
            index: 0,
            getNextIndex: () => {
                return graphColors.contrast.index++ % 3;
            },
        },
    };

    Object.entries(tokenUsage).forEach(([key, value]) => {
        if (value && value > 0) {
            const viewDetails: TokenUsageViewDetails = {
                usageCount: value,
                legendLabel: TokenUsageFunctionNameMap[key],
                color: tailwindPrimaryColors[graphColors.brand.index],
            };

            if (key.toLocaleUpperCase().includes('MEMORY')) {
                memoryGenerationUsage += value;
                viewDetails.color = contrastColors[graphColors.contrast.getNextIndex()];
                memoryGenerationView[key] = viewDetails;
            } else {
                responseGenerationUsage += value;
                graphColors.brand.index = graphColors.brand.getNextIndex();
                responseGenerationView[key] = viewDetails;
            }
        }
    });

    const totalUsage = memoryGenerationUsage + responseGenerationUsage;

    return (
        <>
            <h3 className="font-semibold text-primary-900 dark:text-primary-200 mb-4">
                Token Usage
                {/* <Popover withArrow>
                    <PopoverTrigger disableButtonEnhancement>
                        <Button icon={<Info16 />} appearance="transparent" />
                    </PopoverTrigger>
                    <PopoverSurface className={classes.popover}>
                        <Body1>
                            Token count for each category is the total sum of tokens used for the prompt template and
                            chat completion for the respective completion functions. For more details about token usage,
                            see{' '}
                            <a href="https://learn.microsoft.com/en-us/dotnet/api/azure.ai.openai.completionsusage?view=azure-dotnet-preview">
                                CompletionsUsage
                            </a>{' '}
                            docs.
                        </Body1>
                    </PopoverSurface>
                </Popover> */}
            </h3>
            <div>
                {loadingResponse ? (
                    <p>
                        Final token usage will be available once bot response is generated.
                        <TypingIndicator />
                    </p>
                ) : (
                    <>
                        {totalUsage > 0 ? (
                            <>
                                {!promptView && <p>Total token usage for current session</p>}
                                <div className="flex flex-wrap">
                                    {Object.entries(responseGenerationView).map(([key, details]) => {
                                        return <TokenUsageBar key={key} details={details} totalUsage={totalUsage} />;
                                    })}
                                    {Object.entries(memoryGenerationView).map(([key, details]) => {
                                        return <TokenUsageBar key={key} details={details} totalUsage={totalUsage} />;
                                    })}
                                </div>
                                <div className="flex flex-wrap mt-2">
                                    <TokenUsageLegendItem
                                        key={'Response Generation'}
                                        name={'Response Generation'}
                                        usageCount={responseGenerationUsage}
                                        //items={Object.values(responseGenerationView)}
                                        color={tailwindPrimaryColors[graphColors.brand.legend]}
                                    />
                                    <TokenUsageLegendItem
                                        key={'Memory Generation'}
                                        name={'Memory Generation'}
                                        usageCount={memoryGenerationUsage}
                                        //items={Object.values(memoryGenerationView)}
                                        color={contrastColors[graphColors.contrast.legend]}
                                    />
                                </div>
                            </>
                        ) : promptView ? (
                            <p>No tokens were used. This either is a hardcoded response or saved plan.</p>
                        ) : (
                            <p>No tokens have been used in this session yet.</p>
                        )}
                    </>
                )}
            </div>
            {/* <Divider className={classes.divider} /> */}
            <div className="border-t border-gray-200 dark:border-slate-800 mt-4 mb-4"></div>
        </>
    );
};
